import { datadogRum } from '@datadog/browser-rum'
import axios, { AxiosResponse, CancelToken } from 'axios'
import { useEffect, useRef, useState } from 'react'
import {
  getFilterKey,
  getSelectionsKey
} from '../../../../controls/AnalyticsFilters/AnalyticsFilterPanel.hooks'
import {
  FiltersResponse,
  ForkFilterOption,
  NewFilterOption,
  NewForkFilter,
  SelectedFilter
} from '../../../../data/model/results'
import { usePollOn202 } from '../../../../hooks/usePollOn202'
import {
  RehydrationInProgressError,
  parseRetryAfter
} from '../../../../utils/RehydrationInProgressError'
import { buildRequestUrl, createApiConfig } from '../../../../utils/apiUtils'
import requestWithRetry from '../../../../utils/requestWithRetry'
import { toResponseFilters } from '../../../../utils/resultsUtils'

interface UseFiltersParams {
  surveyId: string
  search?: string
  offset?: number
  limit?: number
  selectedFilters?: SelectedFilter[]
  language?: string
  /** Holds the request until the survey's language is known — useResultsLanguages. */
  languageLoading?: boolean
}

interface UseCombinedFiltersParams extends Omit<UseFiltersParams, 'surveyId'> {
  projectId: string
}

interface UseFiltersReturn {
  error: boolean
  loading: boolean
  /** The page currently loaded, i.e. what the "Add filters..." list offers. */
  filters: NewFilterOption[]
  forkFilters: ForkFilterOption[]
  /**
   * Every filter seen so far, which the selected-filter cards render from. A
   * selection outlives the page it was made on — searching, or ticking an
   * option, refetches from offset 0 — and a card with no definition to render
   * would leave that filter applied to the results but invisible, with no way
   * to untick it. Counts come from the last response that carried the filter.
   */
  knownFilters: NewFilterOption[]
  knownForkFilters: ForkFilterOption[]
  totalCount: number
}

const FILTERS_ENDPOINT = `${
  process.env.REACT_APP_FD_CORE_RESPONSES_URL || '/v1/core-responses'
}/analytics/survey`

// Combined (multi-market) filters are keyed on the project rather than a survey:
// the backend resolves the project to its market surveys and lists filters over
// the same pooled question universe the combined results endpoint charts. Hence
// the plural "surveys" route taking a projectId body, not a surveyId path segment.
const COMBINED_FILTERS_ENDPOINT = `${
  process.env.REACT_APP_FD_CORE_RESPONSES_URL || '/v1/core-responses'
}/analytics/surveys/filters`

/**
 * Shared fetch/commit machinery behind the per-survey and combined filter
 * panels. Both poll the same 202-rehydration contract, paginate by appending
 * and surface the same view model, so only the request itself differs: callers
 * pass a fetcher plus the deps that should re-trigger it.
 */
const useFiltersRequest = (
  fetchFilters: (
    cancelToken: CancelToken
  ) => Promise<FiltersResponse | undefined>,
  deps: unknown[],
  {
    offset,
    listKey,
    languageLoading,
    errorMessage,
    errorContext
  }: {
    offset: number
    /** Identifies the current result set, i.e. every request criterion but offset. */
    listKey: string
    languageLoading: boolean
    errorMessage: string
    errorContext: Record<string, unknown>
  }
): UseFiltersReturn => {
  const [error, setError] = useState<boolean>(false)
  const [filters, setFilters] = useState<NewFilterOption[]>([])
  const [forkFilters, setForkFilters] = useState<ForkFilterOption[]>([])
  const [knownFilters, setKnownFilters] = useState<NewFilterOption[]>([])
  const [knownForkFilters, setKnownForkFilters] = useState<ForkFilterOption[]>(
    []
  )
  const [totalCount, setTotalCount] = useState<number>(0)

  // Read at report time rather than depended on: the context is rebuilt every
  // render, so keeping it out of the effect's deps stops one failure from being
  // reported again on every unrelated re-render.
  const errorContextRef = useRef(errorContext)
  errorContextRef.current = errorContext

  // Filters polling has no separate UI — the chart-list hook surfaces the
  // page-level "preparing your data" banner, and both endpoints 202 in
  // lockstep, so duplicate messaging here would be redundant noise.
  const {
    status,
    data,
    error: apiError
  } = usePollOn202<FiltersResponse>(
    (signal) => {
      const source = axios.CancelToken.source()
      if (signal.aborted) source.cancel()
      else
        signal.addEventListener('abort', () => source.cancel(), { once: true })
      return fetchFilters(source.token)
    },
    deps,
    // Also in `deps` above: usePollOn202 omits `enabled` from its effect deps,
    // so flipping it alone would never re-trigger the fetch.
    { enabled: !languageLoading }
  )

  // Commit a fresh fetch's payload only when status flips to 'success'. While
  // re-fetching (status === 'loading') `data` still holds the prior result, so
  // each response is committed exactly once: without that guard, bumping the
  // offset for "load more" re-runs this effect against the page already held
  // and appends it to itself before the next page has even arrived.
  const committedDataRef = useRef<FiltersResponse | undefined>(undefined)
  const committedListKeyRef = useRef<string>(listKey)

  useEffect(() => {
    if (status !== 'success' || !data || committedDataRef.current === data) {
      return
    }

    // Only a later page of the same result set extends the list. A response
    // fetched under new criteria replaces it, however far the offset had got.
    const isNextPage = offset > 0 && listKey === committedListKeyRef.current
    committedDataRef.current = data
    committedListKeyRef.current = listKey

    setError(false)
    setTotalCount(data.totalFiltersCount)

    // Retained across pages and searches, newest definition winning so counts
    // stay fresh for anything the latest response carried.
    setKnownFilters((prevKnown) => {
      const byKey = new Map(
        prevKnown.map((filter) => [
          getFilterKey(filter.questionId, filter.marketLabel),
          filter
        ])
      )
      data.filters.forEach((filter) =>
        byKey.set(getFilterKey(filter.questionId, filter.marketLabel), filter)
      )
      return [...byKey.values()]
    })
    setKnownForkFilters((prevKnown) => {
      const byId = new Map(prevKnown.map((fork) => [fork.id, fork]))
      data.forkFilters.forEach((fork) => byId.set(fork.id, fork))
      return [...byId.values()]
    })

    if (isNextPage) {
      // Sections the API returns wholesale rather than sliced repeat on every
      // page: fork filters always, plus the combined view's synthetic Market
      // question, which is prepended to each page. Appending blindly would show
      // them once per page loaded, so the page only contributes rows the list
      // does not already hold, and the fork list is taken from the latest
      // response, which is already complete.
      setFilters((prevFilters) => {
        const seen = new Set(
          prevFilters.map((filter) =>
            getFilterKey(filter.questionId, filter.marketLabel)
          )
        )
        return [
          ...prevFilters,
          ...data.filters.filter(
            (filter) =>
              !seen.has(getFilterKey(filter.questionId, filter.marketLabel))
          )
        ]
      })
      setForkFilters(data.forkFilters)
    } else {
      setFilters(data.filters)
      setForkFilters(data.forkFilters)
    }
  }, [status, data, offset, listKey])

  useEffect(() => {
    if (status !== 'error') return
    setError(true)
    datadogRum.addError(new Error(errorMessage), {
      ...errorContextRef.current,
      apiError
    })
  }, [status, apiError, errorMessage])

  return {
    error,
    // While gated, usePollOn202 sits at 'idle' — report that as loading so the
    // panel keeps its spinner instead of flashing an empty filter list.
    loading:
      languageLoading || status === 'loading' || status === 'rehydrating',
    filters,
    forkFilters,
    knownFilters,
    knownForkFilters,
    totalCount
  }
}

export const useAnalyticsFilters = ({
  surveyId,
  search,
  offset = 0,
  limit,
  selectedFilters,
  language,
  languageLoading = false
}: UseFiltersParams): UseFiltersReturn =>
  useFiltersRequest(
    (cancelToken) =>
      getAnalyticsFiltersData(
        surveyId,
        search,
        offset,
        limit,
        selectedFilters,
        cancelToken,
        language
      ),
    [
      surveyId,
      search,
      offset,
      limit,
      selectedFilters,
      language,
      languageLoading
    ],
    {
      offset,
      listKey: [
        surveyId,
        search,
        limit,
        language,
        getSelectionsKey(selectedFilters ?? [])
      ].join('|'),
      languageLoading,
      errorMessage: 'Analytics filters API failed',
      errorContext: { surveyId, search, offset, limit, selectedFilters }
    }
  )

export const useCombinedAnalyticsFilters = ({
  projectId,
  search,
  offset = 0,
  limit,
  selectedFilters,
  language,
  languageLoading = false
}: UseCombinedFiltersParams): UseFiltersReturn =>
  useFiltersRequest(
    (cancelToken) =>
      getCombinedFiltersData(
        projectId,
        search,
        offset,
        limit,
        selectedFilters,
        cancelToken,
        language
      ),
    [
      projectId,
      search,
      offset,
      limit,
      selectedFilters,
      language,
      languageLoading
    ],
    {
      offset,
      listKey: [
        projectId,
        search,
        limit,
        language,
        getSelectionsKey(selectedFilters ?? [])
      ].join('|'),
      languageLoading,
      errorMessage: 'Combined analytics filters API failed',
      errorContext: { projectId, search, offset, limit, selectedFilters }
    }
  )

/**
 * Shapes the panel's selections into the request body both filters endpoints
 * share. Every selected question contributes its id to `questionIds` so the
 * response carries counts for it, while only questions with actual option
 * selections become `responseFilters`. `marketLabel` is deliberately not sent:
 * it disambiguates market-split cards in the UI and is not part of the API
 * contract — the backend scopes a split cluster by its option ids alone.
 */
const buildFiltersRequestBody = (
  search: string | undefined,
  offset: number | undefined,
  limit: number | undefined,
  selectedFilters: SelectedFilter[] | undefined,
  language: string | undefined,
  forkFilters: NewForkFilter[]
) => {
  const selected = selectedFilters ?? []

  const selectedQuestionIds = selected
    .map((filter) => filter.questionId)
    .filter((questionId): questionId is string => !!questionId)

  return {
    search: search || null,
    offset,
    limit: limit || null,
    questionIds:
      selectedQuestionIds.length > 0 ? [...new Set(selectedQuestionIds)] : null,
    responseFilters: toResponseFilters(selectedFilters),
    forkFilters,
    // Omitted when unknown: the API validates the BCP-47 shape, and leaving it
    // out keeps the existing (English-canonical) resolution behaviour.
    ...(language ? { language } : {})
  }
}

const postFilters = async (
  endpoint: string,
  requestBody: object,
  cancelToken?: CancelToken
): Promise<FiltersResponse | undefined> =>
  requestWithRetry({
    method: 'post',
    url: buildRequestUrl(endpoint, process.env.REACT_APP_REST_DOMAIN),
    payload: requestBody,
    getConfig: () => createApiConfig(cancelToken)
  })
    .then((response: AxiosResponse<FiltersResponse>) => {
      if (response.status === 202) {
        throw new RehydrationInProgressError(
          parseRetryAfter(response.headers['retry-after'])
        )
      }
      return response.data
    })
    .catch((error) => {
      if (axios.isCancel(error)) return undefined
      throw error
    })

/**
 * Shapes the panel's fork selections into the request body both filters
 * endpoints share. In MMv2 every market of a project shares one questionnaire,
 * so a fork has the same forkId in every market and a combined selection can
 * be sent back exactly as it came from the combined filters response — no
 * per-market remapping needed.
 */
const toForkFilters = (
  selectedFilters: SelectedFilter[] | undefined
): NewForkFilter[] =>
  (selectedFilters ?? [])
    .filter((filter) => filter.forkId && filter.branchNumbers?.length)
    .map((filter) => ({
      forkId: filter.forkId as string,
      branchNumbers: filter.branchNumbers?.map(Number) || []
    }))

export const getAnalyticsFiltersData = async (
  surveyId: string,
  search?: string,
  offset?: number,
  limit?: number,
  selectedFilters?: SelectedFilter[],
  cancelToken?: CancelToken,
  language?: string
): Promise<FiltersResponse | undefined> =>
  postFilters(
    `${FILTERS_ENDPOINT}/${surveyId}/filters`,
    buildFiltersRequestBody(
      search,
      offset,
      limit,
      selectedFilters,
      language,
      toForkFilters(selectedFilters)
    ),
    cancelToken
  )

export const getCombinedFiltersData = async (
  projectId: string,
  search?: string,
  offset?: number,
  limit?: number,
  selectedFilters?: SelectedFilter[],
  cancelToken?: CancelToken,
  language?: string
): Promise<FiltersResponse | undefined> =>
  postFilters(
    COMBINED_FILTERS_ENDPOINT,
    {
      projectId,
      ...buildFiltersRequestBody(
        search,
        offset,
        limit,
        selectedFilters,
        language,
        toForkFilters(selectedFilters)
      )
    },
    cancelToken
  )
